import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

interface ReceiptPrintedNoPermissionModalProps {
  open: boolean;
  onClose: () => void;
  tableName?: string;
  receiptPrintedAt?: string;
  receiptPrintedBy?: string;
}

/**
 * Adisyon yazdırıldıktan sonra yetkisi olmayan garsonların
 * ürün eklemesini engelleyen uyarı modalı
 */
const ReceiptPrintedNoPermissionModal: React.FC<ReceiptPrintedNoPermissionModalProps> = ({
  open,
  onClose,
  tableName,
  receiptPrintedAt,
  receiptPrintedBy,
}) => {
  // Tarih formatlama
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Başlık */}
      <DialogTitle
        sx={{
          bgcolor: 'warning.main',
          color: 'warning.contrastText',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 2,
        }}
      >
        <WarningAmberIcon sx={{ fontSize: 32 }} />
        <Typography variant="h6" fontWeight="bold">
          Ürün Ekleme Yetkiniz Bulunmamaktadır
        </Typography>
      </DialogTitle>

      {/* İçerik */}
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 2,
          }}
        >
          {/* İkon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'warning.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 48, color: 'warning.dark' }} />
          </Box>

          {/* Ana Mesaj */}
          <Typography variant="h6" color="text.primary" fontWeight="medium">
            Bu masanın adisyonu yazdırıldı!
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 400, lineHeight: 1.7 }}
          >
            Adisyon yazdırıldığı için bu masaya ürün ekleme yetkiniz bulunmamaktadır. 
            Lütfen yetkili personele talebinizi iletiniz.
          </Typography>

          {/* Detay Bilgiler */}
          {(tableName || receiptPrintedAt || receiptPrintedBy) && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 2,
                width: '100%',
              }}
            >
              {tableName && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Masa:</strong> {tableName}
                </Typography>
              )}
              {receiptPrintedBy && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Adisyonu Yazdıran:</strong> {receiptPrintedBy}
                </Typography>
              )}
              {receiptPrintedAt && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Yazdırma Zamanı:</strong> {formatDate(receiptPrintedAt)}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Butonlar */}
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          sx={{
            py: 1.5,
            fontWeight: 'bold',
            borderRadius: 2,
          }}
        >
          Tamam
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptPrintedNoPermissionModal;
